import React, { useEffect, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { flowResult } from 'mobx'
import { useContentStore } from './useStore'
import { getSearchTopSlot, getRowSlots } from '../../pages/category/partials/display-slots'
import { DefaultContentBody } from 'dc-delivery-sdk-js'
import algoliasearchHelper from 'algoliasearch-helper'


export function useSearchPageContent(results: algoliasearchHelper.SearchResults) {
  const contentStore = useContentStore()
  const { isExact: isSearchPage } = useRouteMatch('/search') || { isExact: false }
  const [searchTopSlot, setSearchTopSlot] = useState<React.ReactNode>(null)
  const [rowSlots, setRowSlots] = useState<Array<DefaultContentBody> | null>(null)
  const [searchBannerContentKey, setSearchBannerContentKey] = useState<string | null>(null)

  useEffect(() => {
    async function fetchSearchPageContent() {
      if (!results || !isSearchPage) {
        setSearchTopSlot(null)
        setRowSlots(null)
        setSearchBannerContentKey(null)
        return
      }

      try {
        // Fetch search page content
        const searchPageContentDeliveryKey = results?.userData?.find(
          (userDataObj: Record<string, unknown>) => userDataObj.searchPageContent
        )
        
        // Ensures we have a hyphenated and whitespace free delivery key to fetch from Amplience
        const deliveryKey = searchPageContentDeliveryKey?.searchPageContent?.trim().replace(" ", "-")

        if (deliveryKey) {
          await flowResult(contentStore.fetchItemByKey(deliveryKey))
          const searchPage = contentStore.contentById[deliveryKey]
          
          if (searchPage) {
            const topSlot = getSearchTopSlot(searchPage)
            const categoryRowSlots = getRowSlots(searchPage)
            setSearchTopSlot(topSlot)
            setRowSlots(categoryRowSlots)
          }
        }
        
        // Reset to null if no delivery key or no search page found
        if (!deliveryKey || !contentStore.contentById[deliveryKey]) {
          setSearchTopSlot(null)
          setRowSlots(null)
        }

        // Fetch search page banner
        const searchPageBannerDeliveryKey = results?.userData?.find(
          (userDataObj: Record<string, unknown>) => userDataObj.bannerDeliveryKey,
        )
        
        if (searchPageBannerDeliveryKey) {
          await flowResult(contentStore.fetchItemByKey(searchPageBannerDeliveryKey.bannerDeliveryKey))
          setSearchBannerContentKey(searchPageBannerDeliveryKey.bannerDeliveryKey as string)
        } else {
          setSearchBannerContentKey(null)
        }

      } catch (error) {
        console.log('Error during fetching Search Results Page content key', error)
        setSearchTopSlot(null)
        setRowSlots(null)
        setSearchBannerContentKey(null)
      }
    }

    fetchSearchPageContent()
  }, [results, isSearchPage])

  return { searchTopSlot, rowSlots, searchBannerContentKey }
}
